/**
 * Spec Console のルート app。
 *
 * ルートはコードベース定義（`createRootRoute` / `createRoute` / `addChildren`）で組む。
 * ファイルベースの route 生成（@tanstack/router-plugin）は使わない。
 * ルートの定義そのものは機能ごとのファイル（`routes/registry/*`）が持ち、ここは並べるだけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business Query provider / Toast provider / router / Faro エラーバウンダリを合成し、機能ごとのルート定義を 1 本のルート木に並べる。
 * @graph-connects none
 */
import { FaroErrorBoundary } from '@cortex/faro/react';
import { ToastProvider } from '@cortex/web-design-system/components/ui/Toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { config } from './config.js';
import { shouldRetryQuery } from './lib/orpc-errors.js';
import { businessLogicRoutes } from './routes/registry/business-logic.js';
import { businessMapRoutes } from './routes/registry/business-map.js';
import { commonRoutes } from './routes/registry/common.js';
import { flowRoutes } from './routes/registry/flow.js';
import { mastersRoutes } from './routes/registry/masters.js';
import { pjRoutes } from './routes/registry/pj.js';
import { rootRoute } from './routes/registry/root.js';

/**
 * 取得の既定。再試行の可否は `shouldRetryQuery` が決める — 4xx を再試行すると、接続状態の
 * 判定が揺れた瞬間に取得が `paused` へ落ちて永久に終わらなくなる（`orpc-errors.ts`）。
 * @graph-connects shouldRetryQuery [calls] 取得の再試行可否
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: config.query.staleTime,
      gcTime: config.query.gcTime,
      retry: shouldRetryQuery(config.query.retry),
      refetchOnWindowFocus: config.query.refetchOnWindowFocus,
    },
  },
});

/**
 * ルート木。`route-paths.ts` の定数と 1 対 1 で並べる。定数だけあって実ルートが無いパスを
 * 作らないことが、深いリンクで 404 を出さないための構造的な担保になる。この 1 対 1 は
 * テストで突合するため、木そのものを公開する。
 *
 * 機能ごとのルートの並び（`registry/*` の `*Routes`）を結合するだけで、個々のルートは書かない。
 * 機能がルートを足すときは自分の `registry/*` に足せば済む。
 * @graph-connects none
 */
export const routeTree = rootRoute.addChildren([
  ...commonRoutes,
  ...pjRoutes,
  ...businessMapRoutes,
  ...flowRoutes,
  ...businessLogicRoutes,
  ...mastersRoutes,
]);
/** @graph-connects none */
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business Spec Console のルート app。Query provider / Toast provider / router / Faro エラーバウンダリを合成する。
 * @graph-connects none
 */
export function App() {
  return (
    <FaroErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </FaroErrorBoundary>
  );
}
